import type {
  ConditionMultiselectRangeValue,
  ConditionOption,
  ConditionRangeValue,
  ConditionSection,
  ConditionSelection,
  ConditionSelectionType,
  ConditionValue,
  UnitValueRule,
} from '../conditions.types';
import { toRangeLabel } from './toRangeLabel';

export const CONDITION_SELECTION_TYPES: ConditionSelectionType[] = [
  'includes',
  'excludes',
];

/** A section's items are homogeneous, so the first entry decides the list type */
export const isSelectValue = (
  value: ConditionValue | undefined
): value is string[] =>
  Array.isArray(value) && (value.length === 0 || typeof value[0] === 'string');

const isUnitValue = (
  value: ConditionValue | undefined
): value is UnitValueRule[] =>
  Array.isArray(value) && (value.length === 0 || typeof value[0] === 'object');

/**
 * A range per item, told apart from a single `ConditionRangeValue` by what its
 * entries hold: ranges under keys rather than bounds. An empty map reads as an
 * empty range, which both shapes treat the same way.
 */
export const isMultiselectRangeValue = (
  value: ConditionValue | undefined
): value is ConditionMultiselectRangeValue => {
  if (value === undefined || Array.isArray(value)) {
    return false;
  }

  const entries = Object.values(value);

  return (
    entries.length > 0 &&
    entries.every((entry) => typeof entry === 'object' && entry !== null)
  );
};

export const isRangeValue = (
  value: ConditionValue | undefined
): value is ConditionRangeValue =>
  value !== undefined &&
  !Array.isArray(value) &&
  !isMultiselectRangeValue(value);

/** Stable id for a rule, used for React keys and matching against selections */
export const unitValueKey = (rule: UnitValueRule): string =>
  `${rule.value}${rule.units}`;

const UNIT_VALUE_KEY = /^(-?\d*\.?\d+)(\D.*)$/;

/**
 * Inverse of {@link unitValueKey}, for reading a stored `weight_and_price`
 * selection back out — it is keyed by the option, so the rule it was keyed from
 * has to be rebuilt to persist it. A key that was never a unit value (a section
 * keyed by opaque strings, ex `half_gram`) has no rule to return.
 */
export const parseUnitValueKey = (key: string): UnitValueRule | undefined => {
  const match = UNIT_VALUE_KEY.exec(key);

  return match ? { units: match[2], value: Number(match[1]) } : undefined;
};

/** The string handle a tag carries for an option */
export const optionKey = (option: ConditionOption): string =>
  typeof option.value === 'string' ? option.value : unitValueKey(option.value);

/** An option together with the labels of the options it is nested under */
export type OptionPath = {
  ancestorLabels: string[];
  option: ConditionOption;
};

/** All options as a flat list, sub-items included, each keeping its ancestry */
export const flattenOptionPaths = (
  options: ConditionOption[],
  ancestorLabels: string[] = []
): OptionPath[] =>
  options.flatMap((option) => [
    { ancestorLabels, option },
    ...flattenOptionPaths(option.subItems ?? [], [
      ...ancestorLabels,
      option.label,
    ]),
  ]);

/** All options as a flat list, sub-items included */
export const flattenOptions = (options: ConditionOption[]): ConditionOption[] =>
  flattenOptionPaths(options).map(({ option }) => option);

/** A nested option reads with its parents, ex `Edible/Chocolate` */
export const toBreadcrumbLabel = ({
  ancestorLabels,
  option,
}: OptionPath): string => [...ancestorLabels, option.label].join('/');

/** Whether an option's value is covered by a section's selection */
export const matchesValue = (
  value: string | UnitValueRule,
  selected: ConditionValue | undefined
): boolean => {
  // only the list shapes carry options; a range of either kind covers none
  if (selected === undefined || !Array.isArray(selected)) {
    return false;
  }

  return typeof value === 'string'
    ? isSelectValue(selected) && selected.includes(value)
    : isUnitValue(selected) &&
        selected.some((rule) => unitValueKey(rule) === unitValueKey(value));
};

/** The labels of every option the section's selection covers */
export const getSelectedLabels = (
  options: ConditionOption[],
  selected: ConditionValue | undefined
): Set<string> =>
  new Set(
    flattenOptions(options)
      .filter((option) => matchesValue(option.value, selected))
      .map((option) => option.label)
  );

/** Narrows a homogeneous option list back to the shape the section persists */
export const toConditionValue = (options: ConditionOption[]): ConditionValue =>
  options.every((option) => typeof option.value === 'string')
    ? (options.map((option) => option.value) as string[])
    : (options.map((option) => option.value) as UnitValueRule[]);

/** A selection reduced to what a tag renders and removes by */
export type SelectedItem = { label: string; value: string };

export const getSelectedItems = (
  selection: ConditionSelection,
  selectionType: ConditionSelectionType,
  section: ConditionSection
): SelectedItem[] => {
  if (section.type === 'range') {
    return [];
  }

  const selected = selection[selectionType][section.key];

  // one tag per selected row, reading the range it was narrowed to
  if (section.type === 'multiselect-range') {
    if (!isMultiselectRangeValue(selected)) {
      return [];
    }

    return section.items.flatMap((option) => {
      const range = selected[optionKey(option)];

      return range === undefined
        ? []
        : [
            {
              label: `${option.label}${toRangeLabel(range, section) && ` (${toRangeLabel(range, section)})`}`,
              value: optionKey(option),
            },
          ];
    });
  }

  // a sub-item's label is ambiguous on its own, so it carries its parents
  return flattenOptionPaths(section.items)
    .filter(({ option }) => matchesValue(option.value, selected))
    .map((path) => ({
      label: toBreadcrumbLabel(path),
      value: optionKey(path.option),
    }));
};

export const removeSelectedValue = (
  selection: ConditionSelection,
  selectionType: ConditionSelectionType,
  section: ConditionSection,
  value: string
): ConditionValue => {
  const current = selection[selectionType][section.key];

  if (section.type === 'multiselect-range') {
    if (!isMultiselectRangeValue(current)) {
      return current ?? {};
    }

    const remaining = { ...current };

    delete remaining[value];

    return remaining;
  }

  if (section.type === 'range' || isRangeValue(current)) {
    return current ?? {};
  }

  // Filters the saved value, not `section.items` — a section whose items are
  // just its own selection would drop anything not hydrated yet
  if (isSelectValue(current)) {
    return current.filter((selected) => selected !== value);
  }

  return isUnitValue(current)
    ? current.filter((rule) => unitValueKey(rule) !== value)
    : (current ?? []);
};

/** A section's selections of a single type, as a summary reads them */
export type ConditionSummaryRow = {
  label: string;
  selectionType: ConditionSelectionType;
  values: string[];
};

const getSummaryValues = (
  selection: ConditionSelection,
  selectionType: ConditionSelectionType,
  section: ConditionSection
): string[] => {
  if (section.type === 'range') {
    const value = selection[selectionType][section.key];

    return isRangeValue(value)
      ? [toRangeLabel(value, { allowedDecimalPlaces: 2, startUnit: '$' })]
      : [];
  }

  return getSelectedItems(selection, selectionType, section).map(
    (item) => item.label
  );
};

/** One row per section and selection type holding a selection */
export const getConditionSummaryRows = (
  conditions: ConditionSection[],
  selection: ConditionSelection
): ConditionSummaryRow[] =>
  conditions.flatMap((section) =>
    CONDITION_SELECTION_TYPES.flatMap((selectionType) => {
      const values = getSummaryValues(selection, selectionType, section);

      return values.length > 0
        ? [
            {
              label: section.summaryLabel ?? section.label,
              selectionType,
              values,
            },
          ]
        : [];
    })
  );

export const getSelectedCount = (
  selection: ConditionSelection,
  selectionType: ConditionSelectionType,
  section: ConditionSection
): number => {
  const value = selection[selectionType][section.key];

  if (value === undefined) {
    return 0;
  }

  if (isMultiselectRangeValue(value)) {
    return Object.keys(value).length;
  }

  if (!isRangeValue(value)) {
    return value.length;
  }

  return value.max === undefined && value.min === undefined ? 0 : 1;
};
